import { type Restaurant } from "api-types";
import { User } from "firebase/auth";
import { useDeviceDimensions } from "igfd-ui";

import React, { PropsWithChildren } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { Box, Drawer } from "@mui/material";

import { useDisclosure } from "@dashboard/hooks/useDisclosure";
import { type ReduxState } from "@dashboard/types";
import { isAdminShellPath } from "@dashboard/utils/isAdminShellPath";

import { HeaderComponent } from "../header/HeaderComponent";
import { HeaderSidebarComponent } from "../headerSidebar/HeaderSidebarComponent";

export type AppScaffoldProps = PropsWithChildren<{
  pathname: string;
  navigate: (to: string) => void;
  logout: () => void;
  managerDisplayName?: string;
  managerPhotoUrl?: string;
  selectedRestaurant: Restaurant | null;
}>;

/**
 * Renders the authenticated app, with Headers, Sidebar, and the main content.
 */
export const AppScaffoldComponent = ({
  children,
  pathname,
  navigate,
  managerDisplayName,
  managerPhotoUrl,
  selectedRestaurant,
  logout,
}: AppScaffoldProps) => {
  const { isLargeScreen } = useDeviceDimensions();
  const isFullScreen = useSelector(
    (state: ReduxState) => state.dashboardApp.isFullScreen
  );
  // The admin shell renders its own sidebar, so hide the workspace nav under
  // /admin.
  const isAdminPath = isAdminShellPath(pathname);

  const { isOpen: isLeftDrawerOpen, toggle: toggleLeftDrawer } = useDisclosure({
    isDefaultOpen: false,
  });

  return (
    <Box sx={{ minHeight: "100vh", width: "100%", overflowX: "hidden" }}>
      {!isFullScreen ? (
        <>
          <HeaderComponent
            isLargeScreen={isLargeScreen}
            isLeftDrawerOpen={isLeftDrawerOpen}
            toggleLeftDrawer={toggleLeftDrawer}
            managerDisplayName={managerDisplayName}
            managerPhotoUrl={managerPhotoUrl}
            selectedRestaurant={selectedRestaurant}
            logout={logout}
            navigate={navigate}
          />
          {selectedRestaurant && !isAdminPath && (
            <nav>
              <Drawer
                anchor={"left"}
                variant={isLargeScreen ? "permanent" : "temporary"}
                open={isLargeScreen || isLeftDrawerOpen}
                onClose={toggleLeftDrawer}
                PaperProps={{
                  sx: ({ spacing, palette }) =>
                    isLargeScreen
                      ? {
                          // set the height of the drawer to the height
                          // of the viewport minus the height of the header
                          // in large screens
                          height: `calc(100% - ${spacing(8)} + 1px)`,
                          top: `calc(${spacing(8)} + 1px)`,
                          borderRight: `1px solid ${palette.grey[400]}`,
                        }
                      : {},
                }}
              >
                <HeaderSidebarComponent
                  isLargeScreen={isLargeScreen}
                  isLeftDrawerOpen={isLeftDrawerOpen}
                  toggleLeftDrawer={toggleLeftDrawer}
                  pathname={pathname}
                  navigate={navigate}
                  selectedRestaurant={selectedRestaurant}
                />
              </Drawer>
            </nav>
          )}
        </>
      ) : null}
      <Box
        component="main"
        sx={({ spacing }) => {
          const headerOffset = isFullScreen ? "0px" : spacing(8);
          const contentMinHeight = isFullScreen
            ? "100vh"
            : `calc(100vh - ${headerOffset})`;
          // The content column's left offset (the sidebar, hidden on small
          // screens), also a CSS var so a fixed descendant can align to the column.
          const contentLeft =
            isLargeScreen && !isFullScreen && !isAdminPath
              ? spacing(11)
              : "0px";

          return {
            "--dashboard-content-min-height": contentMinHeight,
            "--dashboard-content-left": contentLeft,
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            width: "100%",
            maxWidth: "100%",
            overflowX: "hidden",
            paddingTop: isFullScreen ? 0 : headerOffset,
            paddingLeft: contentLeft,

            // Many route pages use minHeight: 100vh. Inside the authenticated
            // scaffold that adds the header height again and creates phantom
            // scrollbars. Route roots should fill the visible content area.
            "&& > *": {
              minHeight: "var(--dashboard-content-min-height)",
            },
          };
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export const AppScaffold = ({
  children,
  user,
  logout,
}: PropsWithChildren<{
  logout: () => void;
  user: User;
}>) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const selectedRestaurant = useSelector(
    (state: ReduxState) => state.restaurant.selectedRestaurant
  );

  React.useEffect(() => {
    const sectionForPath = (path: string) => {
      if (path.startsWith("/menus")) return "Menus";
      if (path.startsWith("/dishes")) return "Dishes";
      if (path.startsWith("/products")) return "Products";
      if (path.startsWith("/menu-urls")) return "Menu URLs";
      if (path.startsWith("/themes")) return "Themes";
      if (path.startsWith("/settings")) return "Settings";
      if (path.startsWith("/analytics")) return "Analytics";
      if (path.startsWith("/integrations")) return "Integrations";
      return undefined;
    };
    const section = sectionForPath(pathname);
    const restaurantName = selectedRestaurant?.name?.trim();
    const base = "Ingredifind";
    let title: string;
    if (restaurantName && section) title = `${restaurantName} | ${section}`;
    else if (restaurantName) title = restaurantName;
    else if (section) title = `${base} | ${section}`;
    else title = base;
    if (document.title !== title) document.title = title;
  }, [pathname, selectedRestaurant?.name]);

  return (
    <AppScaffoldComponent
      pathname={pathname}
      navigate={navigate}
      managerDisplayName={user.displayName || undefined}
      managerPhotoUrl={user.photoURL || undefined}
      selectedRestaurant={selectedRestaurant}
      logout={logout}
    >
      {children}
    </AppScaffoldComponent>
  );
};
